// @flow
import http from 'axios'
import { root } from './conf'
import qs from 'query-string'
import { fixedEncodeURIComponent } from 'utility'
import Typenames from './Typenames'
import ErrorCodes from './ErrorCodes'
import _ from 'lodash'

/**
 * Creates a new item of given typename
 * @function
 * @memberof HttpService
 *
 * @return { Promise<PostResponse> } The id and version of the new item.
 */
const createItem = (typename: string, item: Object): Promise<PostResponse> => {
  const descriptive = { ...item }
  if (typename !== Typenames.TABLE) {
    delete descriptive.name
  }
  delete descriptive.version

  const body = {
    item: {
      name: item.name,
      descriptive: descriptive
    },
    typename: typename
  }

  return sendPostProvide(body)
}

/**
 * Updates an item of given typename and given id
 * @function
 * @memberof HttpService
 *
 * @return { Promise<PostResponse> } The id and version of the new item.
 */
const updateItem = (typename: string, id: string, item: Object): Promise<PostResponse> => {
  const descriptive: Object = { ...item }
  delete descriptive.id
  if (typename !== Typenames.TABLE) {
    delete descriptive.name
  }
  delete descriptive.version

  const body = {
    id: id,
    item: {
      name: item.name,
      version: item.version,
      descriptive: descriptive
    },
    typename: typename
  }

  return sendPostProvide(body)
}

/**
 * Deletes an item of given typename and given id
 * @function
 * @memberof HttpService
 *
 * @return { Promise<any> }
 */
const deleteItem = (typename: string, id: string): Promise<any> => {
  const body = {
    typename: typename,
    identifier: id
  }

  return http.post(`${root}/withdrawbyid`, body)
}

/**
 * Gets item of given typename by id.
 * @function
 * @memberof HttpService
 *
 * @return { Promise<<Object>> } The item of given typename.
 */
const getById = (typename: string, id: string): Promise<Object> => {
  const params = {
    typename: typename,
    identifier: id
  }
  const url = `${root}/byid?${qs.stringify(params)}`

  return http
    .get(url)
    .then(res => {
      return res.data
        ? {
            ...res.data.descriptive,
            name: res.data.name,
            id: res.data.id,
            version: res.data.version
          }
        : undefined
    })
    .catch(error => console.log(error))
}

/**
 * Gets item of given typename by a property.
 * @function
 * @memberof HttpService
 *
 * @return { Promise<<Object>> } The item of given typename.
 */
const getByPropertyValue = (
  typename: string,
  propertyName: string,
  propertyValue: any
): Promise<Object> => {
  const params = {
    typename: typename
  }
  const queryValue = typeof propertyValue === 'string' ? `'${propertyValue}'` : propertyValue
  const query = `${fixedEncodeURIComponent(`${propertyName}=`)}${queryValue}`
  const url = `${root}/?${qs.stringify(params)}&kql=${query}`

  return http
    .get(url)
    .then(res => {
      if (res.data) {
        return res.data.map(item => {
          return {
            ...item.descriptive,
            name: item.name,
            id: item.id,
            version: item.version
          }
        })
      }
      return []
    })
    .catch(error => console.log(error))
}

/**
 * Gets item of given typename by its name.
 * @function
 * @memberof HttpService
 *
 * @return { Promise<Array<Object>> } The item of given typename.
 */
const getByName = (typename: string, name: string): Promise<Array<Object>> => {
  const params = {
    typename: typename,
    name: name
  }
  const url = `${root}/byname?${qs.stringify(params)}`

  return http
    .get(url)
    .then(res => {
      if (res.data) {
        return res.data.map(item => {
          return {
            ...item.descriptive,
            name: item.name,
            id: item.id,
            version: item.version
          }
        })
      }
      return []
    })
    .catch(error => console.log(error))
}

/**
 * Gets all items of given typename.
 * @function
 * @memberof HttpService
 *
 * @return { Promise<Array<Object>> } The list of items.
 */
const getAll = (typename: string): Promise<Array<Object>> => {
  const params = {
    typename: typename
  }

  return http.get(`${root}/oftype?${qs.stringify(params)}`).then(res => {
    if (res.data) {
      return res.data.map(item => {
        return {
          ...item.descriptive,
          name: item.name,
          id: item.id,
          version: item.version
        }
      })
    }
    return []
  })
}

function mapEntityArray(arr) {
  return arr.map(item => {
    return {
      ...item.descriptive,
      name: item.name,
      id: item.id,
      version: item.version
    }
  })
}

const getAllChanged = (typename: string, knownVersions: Object): Promise<Object> => {
  const emptyRes = { results: [], deleted: [] }
  return http
    .post(`${root}/oftype`, {
      typename: typename,
      knownVersions: knownVersions || []
    })
    .then(res => {
      if (!res.data) return emptyRes
      if (_.isObject(res.data) && res.data.is_diff) {
        return {
          results: mapEntityArray(res.data.results || []),
          deleted: res.data.deleted || []
        }
      } else if (_.isArray(res.data)) {
        return {
          results: mapEntityArray(res.data),
          deleted: []
        }
      }
      return emptyRes
    })
}

/**
 * Gets item of given typename assigned to application with given application id.
 * @function
 * @memberof HttpService
 *
 * @return { Promise<Array<Object>> } The items of given typename assigned to given application id.
 */
const getForApp = (typename: string, appId: string): Promise<Array<Object>> => {
  const params = {
    typename: typename
  }
  const query = `${fixedEncodeURIComponent('application=')}'${appId}'`
  const url = `${root}/querydescriptive?${qs.stringify(params)}&kql=${query}`

  return http.get(url).then(res => {
    return (res.data || []).map(item => {
      return {
        ...item.descriptive,
        name: item.name,
        id: item.id,
        version: item.version
      }
    })
  })
}

/**
 * Gets item of given typename assigned to application with given application id.
 * @function
 * @memberof HttpService
 *
 * @return { Promise<Array<Object>> } The items of given typename assigned to given application id.
 */
const getForMaintainer = (typename: string, userId: string): Promise<Array<Object>> => {
  const params = {
    typename: typename
  }
  const query = `${fixedEncodeURIComponent('maintainer=')}'${userId}'`
  const url = `${root}/querydescriptive?${qs.stringify(params)}&kql=${query}`

  return http.get(url).then(res => {
    return (res.data || []).map(item => {
      return {
        ...item.descriptive,
        name: item.name,
        id: item.id,
        version: item.version
      }
    })
  })
}

/**
 * Handles http error in generic way
 * @function
 * @memberof HttpService
 *
 * @return { Array } Empty array or throws an error
 */
const handleError = (err: any) => {
  if (err && err.response && err.response.status === 404) {
    console.error('Handle error', err)
    return []
  }
  throw err
}

/**
 * Sends post request to provide endpoint (create/update)
 * @function
 * @memberof HttpService
 *
 * @return { Promise<PostResponse> } Id and version of updated/created obejct
 */
const sendPostProvide = (body: Object): Promise<PostResponse> => {
  return http
    .post(`${root}/provide`, body)
    .then(res => {
      return { id: res.data.identifier, version: res.data.version }
    })
    .catch(err => {
      if (err.response.status === 409) {
        return Promise.reject({ code: ErrorCodes.ITEM_OUTDATED, err: err })
      } else {
        return Promise.reject(err)
      }
    })
}

const self = {
  createItem,
  getById,
  getAll,
  getForApp,
  getForMaintainer,
  getByPropertyValue,
  getByName,
  updateItem,
  deleteItem,
  handleError,
  getAllChanged
}

/**
 * Http service: Service for http related operations e.g. handling http errors in special manner
 *
 * @module HttpService
 * @namespace HttpService
 * @version 1.0.0
 */
export default self
